import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, ShoppingCart, ExternalLink, Code, Download } from 'lucide-react';
import SEO from '@/components/common/SEO';

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');

  const fetchProducts = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12',
        sortBy,
        order: sortOrder,
        ...(selectedCategory && { category: selectedCategory }),
        ...(searchQuery && { search: searchQuery })
      });

      const response = await fetch(`/.netlify/functions/products?${params}`);
      const data = await response.json();

      if (data.success && data.data) {
        setProducts(Array.isArray(data.data.items) ? data.data.items : []);
        setPagination(data.data.pagination || {});
        if (data.data.pagination?.categories) {
          setCategories(Array.isArray(data.data.pagination.categories) ? data.data.pagination.categories : []);
        }
      } else {
        setProducts([]);
        setPagination({});
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(1);
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    setSearchParams(params);
  }, [searchQuery, selectedCategory, sortBy, sortOrder]);

  const formatPrice = (price, currency = 'GHS') => {
    if (currency === 'GHS') {
      return `GH₵${price.toLocaleString()}`;
    }
    return `$${price.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white pt-20">
      {/* SEO Meta Tags */}
      <SEO
        title="Premium Software & Digital Products"
        description="Browse and purchase premium web applications, mobile apps, templates, and digital products. High-quality software solutions built with modern technologies."
        image="/images/shop-banner.jpg"
        url="/shop"
      />

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-400"
          >
            Premium Software
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Get full source code access to production-ready applications
          </motion.p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="px-4 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500"
                />
              </div>

              {/* Category Filter */}
              <div className="relative">
                <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 appearance-none cursor-pointer"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
                >
                  <option value="created_at">Latest</option>
                  <option value="price">Price</option>
                  <option value="name">Name</option>
                  <option value="downloads">Popular</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
                >
                  <option value="DESC">↓</option>
                  <option value="ASC">↑</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="px-4 pb-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-lg overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-700"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-2xl text-gray-400">No products found</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                }}
                className="mt-4 text-emerald-400 hover:text-emerald-300"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {products.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-800 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl group flex flex-col"
                  >
                    {/* Product Image */}
                    {product.image_url ? (
                      <div className="h-48 overflow-hidden bg-gray-700">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="h-48 bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Code size={64} className="text-white opacity-50" />
                      </div>
                    )}

                    <div className="p-6 flex flex-col flex-1">
                      {/* Category */}
                      {product.category && (
                        <span className="inline-block w-fit px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs font-semibold mb-3">
                          {product.category}
                        </span>
                      )}

                      {/* Title */}
                      <h3 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-emerald-400 transition-colors">
                        {product.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-400 mb-4 line-clamp-3 flex-1">
                        {product.short_description || product.description}
                      </p>

                      {/* Tech Stack */}
                      {product.tech_stack && product.tech_stack.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {product.tech_stack.slice(0, 3).map((tech) => (
                            <span
                              key={tech}
                              className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300"
                            >
                              {tech}
                            </span>
                          ))}
                          {product.tech_stack.length > 3 && (
                            <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                              +{product.tech_stack.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Meta Info */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-2">
                          <Download size={14} />
                          <span>{product.downloads} downloads</span>
                        </div>
                        {product.version && (
                          <span className="text-gray-400">v{product.version}</span>
                        )}
                      </div>

                      {/* Price and CTA */}
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-700">
                        <div>
                          <span className="text-2xl font-bold text-emerald-400">
                            {formatPrice(parseFloat(product.price), product.currency)}
                          </span>
                        </div>
                        <Link
                          to={`/shop/${product.slug}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold transition-colors"
                        >
                          <ShoppingCart size={18} />
                          View Details
                        </Link>
                      </div>

                      {/* Demo Link */}
                      {product.demo_url && (
                        <a
                          href={product.demo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-3 text-gray-400 hover:text-emerald-400 text-sm transition-colors"
                        >
                          <ExternalLink size={14} />
                          View Live Demo
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="mt-12 flex justify-center items-center gap-2">
                  <button
                    onClick={() => fetchProducts(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 bg-gray-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                  >
                    Previous
                  </button>

                  <div className="flex gap-2">
                    {[...Array(pagination.totalPages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => fetchProducts(i + 1)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          pagination.page === i + 1
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-800 hover:bg-gray-700'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => fetchProducts(pagination.page + 1)}
                    disabled={!pagination.hasMore}
                    className="px-4 py-2 bg-gray-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
